import type { AppliedRestrictions as AppliedRestrictionsType } from '@/api/types'
import { faExternalLink } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from '@tanstack/react-router'
import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DayOfWeek,
  MealOfDayDivision,
  MealPlan,
  RecipeIDObject,
  ScaledRecipe,
  ScaledRecipeIngredient,
} from '@/api/types'
import { Grid } from '@/components/Grid'
import { getIngredientEnergy, getIngredientQuantityText } from '../../utils'
import MealDivisionTypeBadge from '../MealDivisionTypeBadge'
import HouseholdMeals from './HouseholdMeals'
import MealNutrition from './MealNutrition'

import { getTotalHeuristicsScore } from '@/utils/heuristics'
import { toast } from 'react-toastify'
import { GetReplacementsFn } from '../CreateMealPlan'
import { HeuristicScores } from '../HeuristicScores/HeuristicScores'
import { AppliedRestrictions } from './AppliedRestrictions'
import { CollapsibleSection } from '@/components/CollapsibleSection/CollapsibleSection'
import './MealDetail.css'
import { RecipeReplacements } from './RecipeReplacements'

type Props = {
  dayPlan: MealPlan
  mainUserId: number
  getReplacements: GetReplacementsFn
}

function getTotalRemoved(appliedRestrictions: AppliedRestrictionsType) {
  return (
    Object.values(appliedRestrictions.removedCandidates).reduce((acc, val) => acc + val, 0) +
    appliedRestrictions.removedTypesOtherMealDivisions
  )
}

export default function MealDetail({ dayPlan, mainUserId, getReplacements }: Props) {
  const { t } = useTranslation()

  const [isLoadingReplacements, setIsLoadingReplacements] = useState(false)
  const [replacements, setReplacements] = useState<Array<ScaledRecipe> | undefined>(undefined)

  const getReplacementsFn = useCallback(
    (mealDivision: MealOfDayDivision, dayOfWeek: DayOfWeek, recipe: RecipeIDObject) => {
      setIsLoadingReplacements(true)
      setReplacements(undefined)

      getReplacements(mealDivision, dayOfWeek, { idRecipe: recipe.idRecipe })
        .then((replacements) => {
          setReplacements(replacements)
          setIsLoadingReplacements(false)
        })
        .catch(() => {
          setIsLoadingReplacements(false)
          toast.error('Error creating plan')
        })
    },
    [getReplacements],
  )

  return (
    <div className="MealDetail">
      <div style={{ flexGrow: 1 }}>
        {dayPlan.scaledRecipes.map((sr, i) => (
          <div key={sr.recipe.name} className="MealDetail_recipe">
            <div className="MealDetail-recipeHeader">
              <div className="MealDetail-recipeTitle">
                {sr.recipe.name}
                {sr.recipe.idRecipe && (
                  <Link
                    to="/recipes/$recipeId"
                    params={{ recipeId: sr.recipe.idRecipe.toString() }}>
                    <FontAwesomeIcon
                      icon={faExternalLink}
                      size="xs"
                      color="var(--highlight-background)"
                    />
                  </Link>
                )}
              </div>
              <MealDivisionTypeBadge mealDivision={sr.mealOfDayDivision} />
            </div>

            <Grid columns="9fr 2fr 1fr" gap={0} className="MealDetail-gridHeader">
              <div>
                {t('recipe.recipe')}
                <span className="MealDetail-servings">
                  {t('recipe.serving', {
                    count: sr.recipe.preparationDetails?.servings ?? 1,
                  })}
                </span>
              </div>
              <div>Grams</div>
              <div>Kcal</div>
            </Grid>

            <Grid columns="9fr 2fr 1fr" gap={0} className="MealDetail-grid">
              {sr.scaledRecipeIngredients.map((si, i) => (
                <ScaledIngredientRow
                  key={i}
                  scaledIng={si}
                  servings={sr.recipe.preparationDetails?.servings}
                />
              ))}
            </Grid>
            <HouseholdMeals scaledRecipe={sr} />
            {sr.appliedRestrictions && (
              <CollapsibleSection
                label={`Applied Restrictions (candidates: ${sr.appliedRestrictions.initialCandidates}, removed: ${getTotalRemoved(sr.appliedRestrictions)})`}>
                <AppliedRestrictions appliedRestrictions={sr.appliedRestrictions} />
              </CollapsibleSection>
            )}
            {sr.heuristicScores && sr.recipe.idRecipe && (
              <CollapsibleSection
                label={`Heuristic Scores (score: ${getTotalHeuristicsScore(sr.heuristicScores)})`}>
                <HeuristicScores heuristicScores={sr.heuristicScores} />
              </CollapsibleSection>
            )}
            {sr.recipe.idRecipe && (
              <RecipeReplacements
                scaledRecipes={replacements}
                onClick={() => {
                  if (isLoadingReplacements || replacements) return
                  getReplacementsFn(sr.mealOfDayDivision, dayPlan.dayOfWeek, sr.recipe)
                }}
              />
            )}
          </div>
        ))}
      </div>
      <MealNutrition
        style={{ alignSelf: 'start' }}
        scaledRecipes={dayPlan.scaledRecipes}
        mainUserId={mainUserId}
      />
    </div>
  )
}

type IngRowProps = {
  scaledIng: ScaledRecipeIngredient
  servings?: number
}

function ScaledIngredientRow({ scaledIng, servings }: IngRowProps) {
  return (
    <>
      <div>{scaledIng.ingredient.name}</div>
      <div>{getIngredientQuantityText(scaledIng)}</div>
      <div>{getIngredientEnergy(scaledIng)}</div>
    </>
  )
}
